import { PowerConsumptionInfo } from '../types';

/**
 * 能耗监控器
 */
export class PowerMonitor {
  private batteryManager: any = null;
  private cpuUsageHistory: number[] = [];
  private maxHistoryLength = 10;
  private monitoringInterval: number | null = null;
  private onPowerChange?: (info: PowerConsumptionInfo) => void;

  constructor(onPowerChange?: (info: PowerConsumptionInfo) => void) {
    this.onPowerChange = onPowerChange;
    this.initBatteryManager();
  }

  /**
   * 初始化电池管理器
   */
  private async initBatteryManager(): Promise<void> {
    if ('getBattery' in navigator) {
      try {
        this.batteryManager = await (navigator as any).getBattery();
        this.setupBatteryListeners();
      } catch (error) {
        console.warn('Battery API not supported:', error);
      }
    }
  }

  /**
   * 设置电池状态监听器
   */
  private setupBatteryListeners(): void {
    if (!this.batteryManager) return;

    this.batteryManager.addEventListener('levelchange', () => {
      this.collectPowerInfo();
    });

    this.batteryManager.addEventListener('chargingchange', () => {
      this.collectPowerInfo();
    });

    this.batteryManager.addEventListener('chargingtimechange', () => {
      this.collectPowerInfo();
    });

    this.batteryManager.addEventListener('dischargingtimechange', () => {
      this.collectPowerInfo();
    });
  }

  /**
   * 开始能耗监控
   */
  start(intervalMs: number = 5000): void {
    if (this.monitoringInterval) {
      this.stop();
    }

    this.monitoringInterval = window.setInterval(() => {
      this.collectPowerInfo();
    }, intervalMs);

    // 立即收集一次数据
    this.collectPowerInfo();
  }

  /**
   * 停止能耗监控
   */
  stop(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  /**
   * 收集能耗信息
   */
  private collectPowerInfo(): void {
    const powerInfo = this.getPowerConsumptionInfo();
    this.onPowerChange?.(powerInfo);
  }

  /**
   * 获取能耗信息
   */
  getPowerConsumptionInfo(): PowerConsumptionInfo {
    const info: PowerConsumptionInfo = {
      powerLevel: 'medium',
      powerScore: 50
    };

    // 获取电池信息
    if (this.batteryManager) {
      info.batteryLevel = this.batteryManager.level;
      info.charging = this.batteryManager.charging;
      info.timeToEmpty = this.batteryManager.dischargingTime;
      info.timeToFull = this.batteryManager.chargingTime;
    }

    // 估算CPU使用率
    info.cpuUsage = this.estimateCPUUsage();

    // 计算能耗等级和评分
    const { powerLevel, powerScore } = this.calculatePowerMetrics(info);
    info.powerLevel = powerLevel;
    info.powerScore = powerScore;

    return info;
  }

  /**
   * 估算CPU使用率
   */
  private estimateCPUUsage(): number {
    // 基于内存使用率和页面活动来估算CPU使用率
    const memory = (performance as any).memory;
    if (memory) {
      const memoryUsage = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
      
      // 基于内存使用率估算CPU使用率
      let cpuUsage = memoryUsage * 100;
      
      // 考虑页面活动
      const now = performance.now();
      const timeSinceLastActivity = now - (this as any).lastActivityTime || 0;
      
      if (timeSinceLastActivity < 1000) {
        cpuUsage += 20; // 最近有活动，增加CPU使用率
      }
      
      (this as any).lastActivityTime = now;
      
      return Math.min(cpuUsage, 100);
    }
    
    return 0;
  }

  /**
   * 计算能耗指标
   */
  private calculatePowerMetrics(info: PowerConsumptionInfo): { powerLevel: 'low' | 'medium' | 'high'; powerScore: number } {
    let score = 50; // 基础分数

    // 基于电池电量调整分数
    if (info.batteryLevel !== undefined) {
      if (info.batteryLevel < 0.2) {
        score += 30; // 电量低，能耗高
      } else if (info.batteryLevel < 0.5) {
        score += 15;
      } else {
        score -= 10; // 电量充足，能耗相对较低
      }
    }

    // 基于充电状态调整分数
    if (info.charging) {
      score -= 20; // 充电时能耗相对较低
    } else {
      score += 10; // 使用电池时能耗较高
    }

    // 基于CPU使用率调整分数
    if (info.cpuUsage !== undefined) {
      if (info.cpuUsage > 80) {
        score += 25; // CPU使用率高，能耗高
      } else if (info.cpuUsage > 50) {
        score += 10;
      } else {
        score -= 5; // CPU使用率低，能耗低
      }
    }

    // 确保分数在0-100范围内
    score = Math.max(0, Math.min(100, score));

    // 确定能耗等级
    let powerLevel: 'low' | 'medium' | 'high';
    if (score < 30) {
      powerLevel = 'low';
    } else if (score < 70) {
      powerLevel = 'medium';
    } else {
      powerLevel = 'high';
    }

    return { powerLevel, powerScore: score };
  }

  /**
   * 获取CPU使用率历史
   */
  getCPUUsageHistory(): number[] {
    return [...this.cpuUsageHistory];
  }

  /**
   * 清理历史数据
   */
  clearHistory(): void {
    this.cpuUsageHistory = [];
  }

  /**
   * 检查是否支持能耗监控
   */
  isSupported(): boolean {
    return 'getBattery' in navigator || 'memory' in performance;
  }
} 